// @flow
import * as React from 'react'
import { type RouterHistory, type Location } from 'react-router-dom'
import * as userRepo from 'data/user'
import { AppContext, type AppContextValue, withRouter } from 'framework'
// eslint-disable-next-line import/extensions
import AuthModal, { type Step as AuthModalStep } from 'main/components/common/AuthModal'
// eslint-disable-next-line import/extensions
import { IWhitelabelContext } from 'main/framework'
import { directorDashboardHomePath, schoolSearchLocationPath } from 'main/routes'
import { AssetName } from 'main/util/asset'
import { DesktopMediaQuery, MobileMediaQuery } from 'ui/MediaQuery'
import type { User } from 'types'
import {
  adminUrl,
  invoicesUrl,
  logoutUrl,
  paymentMethodUrl,
  referralUrl,
  setupAssistantUrls,
  visionUrl,
} from 'urls'
import { type LinkManifest } from '../common'
import MarcomPageHeaderDesktop from './Desktop'
import MarcomPageHeaderMobile from './Mobile'

type AuthModalState = {
  initialStep: AuthModalStep,
}

export type Filters = {
  location: string,
}

type Props = {
  query?: string,
  whitelabel?: IWhitelabelContext,
}

type InjectedProps = {
  history: RouterHistory,
  location: Location,
}

type InternalProps = Props & InjectedProps

type State = {
  authModal: AuthModalState | null,
  filters: Filters,
}

class MarcomPageHeader extends React.Component<InternalProps, State> {
  static contextType = AppContext
  context: AppContextValue

  constructor(props: InternalProps) {
    super(props)

    this.state = {
      authModal: null,
      filters: {
        location: props.query || '',
      },
    }
  }

  componentWillReceiveProps = (nextProps: InternalProps) => {
    if (nextProps.query !== this.props.query) {
      this.handleUpdateFilters({ location: nextProps.query })
    }
  }

  render = () => {
    const props = {
      user: this.context.user,
      linkManifests: this.linkManifests,
      filters: this.state.filters,
      onUpdateFilters: this.handleUpdateFilters,
      onClearFilters: this.handleClearFilters,
      onSearch: this.handleSearch,
      onRequestLogin: this.handleRequestLogin,
      onRequestSignUp: this.handleRequestSignUp,
    }

    return (
      <React.Fragment>
        <DesktopMediaQuery>
          <MarcomPageHeaderDesktop {...props} />
        </DesktopMediaQuery>

        <MobileMediaQuery>
          <MarcomPageHeaderMobile {...props} />
        </MobileMediaQuery>

        {this.state.authModal && (
          <AuthModal
            initialStep={this.state.authModal.initialStep}
            onAuth={this.handleAuth}
            onRequestClose={this.handleRequestCloseAuthModal}
          />
        )}
      </React.Fragment>
    )
  }

  handleAuth = credentials => {
    const user: User = (credentials.user: any)
    const isDirector = !!user.schoolhouse && !user.schoolhouse.closedAt

    if (isDirector) {
      return window.location.assign(directorDashboardHomePath())
    }

    // HACK (alex): User object in credentials has a different data type than expected by
    // this.context.setUser, so refetch the user before calling it.
    userRepo.fetch(credentials.user.id).then(this.context.setUser)
  }

  handleUpdateFilters = (filters: $Shape<Filters>) => {
    this.setState(prevState => ({
      ...prevState,
      filters: {
        ...prevState.filters,
        ...filters,
      },
    }))
  }

  handleClearFilters = () => {
    this.setState(prevState => ({
      ...prevState,
      filters: {
        ...prevState.filters,
      },
    }))
  }

  handleRequestCloseAuthModal = () => {
    this.setState({ authModal: null })
  }

  handleRequestLogin = () => {
    this.setState({ authModal: { initialStep: 'login' } })
  }

  handleRequestSignUp = () => {
    this.setState({ authModal: { initialStep: 'choose_role' } })
  }

  handleSearch = () => {
    const { location } = this.state.filters
    const query = location.trim()

    const path = schoolSearchLocationPath(query)

    this.props.history.push(path)
  }

  get linkManifests(): LinkManifest[] {
    const user = this.context.user
    const isDirector = user && user.schoolhouse && !user.schoolhouse.closedAt

    if (isDirector) return this.directorLinkManifests

    if (user) return this.parentLinkManifests

    return this.anonymousLinkManifests
  }

  get anonymousLinkManifests(): LinkManifest[] {
    return [
      { href: visionUrl(), text: 'Vision' },
      { onClick: this.handleRequestLogin, text: 'Log In' },
      { onClick: this.handleRequestSignUp, text: 'Sign Up' },
    ]
  }

  get parentLinkManifests(): LinkManifest[] {
    let menuitems = []
    menuitems = menuitems.concat([
      { section: 'My Account', href: '/family/profile', native: true, text: 'Profile' },
      { section: 'My Account', href: '/messages', native: true, text: 'Messages' },
      { section: 'My Account', href: '/me/saved-searches', native: true, text: 'Saved Searches' },
      { section: 'My Account', href: '/family/programs', native: true, text: 'My Programs' },
      { section: 'My Account', href: '/family/children', native: true, text: 'My Children' },
      { section: 'My Account', href: '/family/schedule', native: true, text: 'Schedule' },
      { section: 'My Account', href: invoicesUrl(), text: 'Invoice History' },
      { section: 'My Account', href: paymentMethodUrl(), text: 'Payment Method' },
    ])
    if (
      this.props.whitelabel &&
      this.props.whitelabel.evaluateToTrueExceptIfFalse(AssetName.REFER_A_FRIEND)
    ) {
      menuitems = menuitems.concat([
        { section: 'My Account', href: referralUrl(), text: 'Refer a Friend' },
      ])
    }
    menuitems = menuitems.concat([
      { section: 'My Account', href: '/me/password', native: true, text: 'Update Password' },
    ])
    if (
      this.props.whitelabel &&
      this.props.whitelabel.evaluateToTrueExceptIfFalse(AssetName.REFER_A_FRIEND)
    ) {
      menuitems = menuitems.concat([
        { section: 'My Account', href: '/start', text: 'Start a Wonderschool' },
      ])
    }
    menuitems = menuitems.concat([{ section: 'My Account', href: logoutUrl(), text: 'Log Out' }])

    return menuitems
  }

  get setupAssistantLinkManifests(): LinkManifest[] {
    return [
      { section: 'My Account', href: setupAssistantUrls.index, text: 'Setup Assistant' },
      { section: 'My Account', href: logoutUrl(), text: 'Log Out' },
    ]
  }

  get directorLinkManifests(): LinkManifest[] {
    const topLink = { section: 'My Account', href: adminUrl(), text: 'Program Dashboard' }

    return [topLink, { section: 'My Account', href: logoutUrl(), text: 'Log Out' }]
  }
}

export default (withRouter(MarcomPageHeader): React.ComponentType<Props>)
